import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KoobButton,
  KoobPromptInput,
  KoobPromptSuggestion,
  icons,
} from '@koob/ui';
import AssistantMessage from './AssistantMessage';
import useTypingPlaceholder from './useTypingPlaceholder';
import { scrollToSection } from './sections';

// Below Tailwind's `sm`, the chat covers the screen.
export const isFullScreen = () =>
  !window.matchMedia('(min-width: 640px)').matches;

const fine = () => window.matchMedia('(pointer: fine)').matches;

// One step down when minimized: the chat folds into the card, the card into
// the bar. A phone skips the card, which would cover the page.
const smaller = view => (view === 'chat' && !isFullScreen() ? 'card' : 'bar');

/**
 * Animates the box from its size in the last view to its size in this one
 * (CSS cannot animate to an auto height). Every view keeps the bottom-right
 * corner, so the box grows and shrinks from there. Not into or out of the
 * phone's full screen chat, which moves the box.
 */
function useResize(box, view) {
  const size = useRef(null);
  const last = useRef(view);
  useLayoutEffect(() => {
    const { width, height } = box.current.getBoundingClientRect();
    const from = size.current;
    const was = last.current;
    size.current = { width, height };
    last.current = view;
    if (
      !from ||
      was === view ||
      (isFullScreen() && (was === 'chat' || view === 'chat')) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return;
    box.current.animate(
      [
        { width: `${from.width}px`, height: `${from.height}px` },
        { width: `${width}px`, height: `${height}px` },
      ],
      { duration: 200, easing: 'ease-out' },
    );
  });
}

/**
 * KD-3848 — the assistant, one box floating in the bottom-right corner that
 * grows and shrinks between three views:
 * - bar: the title alone;
 * - card: the title and the box, its placeholder typing out the suggested
 *   questions;
 * - chat: the conversation, the suggested questions just above the box, full
 *   screen on a phone.
 * The box is one element in every view: focusing it in the card grows the
 * chat around it, under the pointer and with the phone keyboard still open.
 * Not modal: the TO keeps reading the factsheet, and a source scrolls it.
 */
export default function AssistantPanel({
  view,
  onViewChange,
  productType,
  productName,
  questions,
  chat,
  onAsk,
  onSearchAvailability,
  tripId,
}) {
  const { t } = useTranslation('assistant');
  const [draft, setDraft] = useState('');
  const end = useRef(null);
  const input = useRef(null);
  const isChat = view === 'chat';
  const title = t('blockTitle', { context: productType });
  const box = useRef(null);
  useResize(box, view);
  const placeholder = useTypingPlaceholder(
    questions,
    t('placeholder', { context: productType }),
    view === 'card',
  );

  // Follows the answer as it streams, then its sources and buttons.
  const last = chat.messages.at(-1);
  useEffect(() => {
    if (isChat) end.current?.scrollIntoView({ block: 'end' });
  }, [chat.messages.length, last?.answer, last?.status, isChat]);

  // The box takes the focus back after each answer (it is disabled while one
  // streams). Not on touch screens, where it opens the keyboard over it.
  useEffect(() => {
    if (isChat && !chat.blocked && fine()) input.current?.focus();
  }, [isChat, chat.blocked]);

  const ask = question => {
    onAsk(question);
    setDraft('');
  };

  // The booking cards sit in the corner the chat covers: fold it to the card
  // first. Full screen hides the whole page: fold to the bar. Then scroll,
  // once the fold has played.
  const showSection = section => {
    const fold = isFullScreen() ? 'bar' : section === 'booking' && 'card';
    if (!fold) return scrollToSection(section);
    onViewChange(fold);
    setTimeout(() => scrollToSection(section), 200);
  };

  // What answer blocks can do on the page (AnswerBlock). A search shows its
  // results in the booking module.
  const actions = {
    productType,
    showSection,
    searchAvailability:
      onSearchAvailability &&
      (search => {
        onSearchAvailability(search);
        showSection('booking');
      }),
  };

  // Nothing to say until a limit is reached.
  let hint = null;
  if (chat.quotaReached) hint = t('quotaReached');
  else if (chat.sessionFull) hint = t('sessionLimit');

  const icon = (
    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
      <i className={icons.sparkles} aria-hidden="true" />
    </span>
  );

  return (
    <section
      role={isChat ? 'dialog' : undefined}
      aria-modal={isChat ? 'false' : undefined}
      aria-labelledby="assistant-title"
      // Not from a date picker's calendar: portalled, it bubbles here too.
      onKeyDown={event =>
        event.key === 'Escape' &&
        isChat &&
        box.current.contains(event.target) &&
        onViewChange(smaller(view))
      }
      ref={box}
      className={`fixed z-floating flex flex-col overflow-hidden bg-white ${
        isChat
          ? 'inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[min(640px,calc(100vh-8rem))] sm:w-[420px] sm:rounded-2xl sm:border sm:border-solid sm:border-gray-200 sm:shadow-2xl'
          : `bottom-4 right-4 rounded-2xl border border-solid border-gray-200 shadow-xl sm:bottom-6 sm:right-6 ${
              view === 'bar' ? 'w-[260px]' : 'w-[calc(100vw-2rem)] sm:w-[420px]'
            }`
      }`}
    >
      {view === 'bar' ? (
        <button
          type="button"
          onClick={() => onViewChange('card')}
          aria-expanded="false"
          className="flex w-full cursor-pointer items-center gap-2.5 border-none bg-transparent px-3 py-2.5 text-left"
        >
          {icon}
          <span
            id="assistant-title"
            className="flex-1 truncate text-sm font-semibold text-gray-900"
          >
            {chat.messages.length
              ? t('showConversation', { count: chat.messages.length })
              : title}
          </span>
          <i
            className={`${icons.chevronUp} px-2 text-xs text-muted-foreground`}
            aria-hidden="true"
          />
        </button>
      ) : (
        <header
          className={`flex items-center gap-2.5 px-3 py-2.5 ${
            isChat ? 'border-0 border-b border-solid border-gray-200' : ''
          }`}
        >
          {icon}
          <span className="flex min-w-0 flex-1 flex-col">
            <span
              id="assistant-title"
              className="text-sm font-semibold text-gray-900"
            >
              {title}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {isChat && productName
                ? productName
                : t('blockSubtitle', { context: productType })}
            </span>
          </span>
          {isChat && chat.messages.length > 0 && (
            <KoobButton
              size="icon"
              variant="subtle"
              aria-label={t('newConversation')}
              title={t('newConversation')}
              disabled={chat.streaming}
              onClick={chat.reset}
            >
              <i className={icons.newChat} />
            </KoobButton>
          )}
          <KoobButton
            size="icon"
            variant="subtle"
            aria-label={t('minimize')}
            title={t('minimize')}
            onClick={() => onViewChange(smaller(view))}
          >
            <i className={icons.chevronDown} />
          </KoobButton>
        </header>
      )}

      {isChat && (
        <p className="m-0 border-0 border-b border-solid border-gray-200 bg-gray-50 px-4 py-1.5 text-center text-[11px] text-muted-foreground">
          {t('notice')}
        </p>
      )}

      {/* The chat's height is fixed: the suggestions going away after the
          first question give their room to the conversation. */}
      {isChat && (
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4"
          aria-live="polite"
        >
          {chat.messages.length > 0 && (
            <div className="flex flex-col gap-6">
              {chat.messages.map(message => (
                <AssistantMessage
                  key={message.id}
                  message={message}
                  chat={chat}
                  tripId={tripId}
                  actions={actions}
                />
              ))}
            </div>
          )}
          <div ref={end} />
        </div>
      )}

      {/* The box stays at the same place in the card and the chat: same side
          and bottom padding, nothing under it. */}
      {view !== 'bar' && (
        <footer
          className={`flex flex-col gap-2 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] ${
            isChat
              ? 'border-0 border-t border-solid border-gray-200 bg-gray-50 pt-3'
              : ''
          }`}
        >
          {isChat && chat.messages.length === 0 && questions.length > 0 && (
            <div className="flex flex-col">
              {questions.map(question => (
                <KoobPromptSuggestion
                  key={question}
                  onClick={() => ask(question)}
                  disabled={chat.blocked}
                >
                  {question}
                </KoobPromptSuggestion>
              ))}
            </div>
          )}
          <KoobPromptInput
            ref={input}
            size={isChat ? 'md' : 'sm'}
            value={draft}
            onValueChange={setDraft}
            onSubmit={ask}
            onFocus={() => view === 'card' && onViewChange('chat')}
            sendLabel={t('send')}
            placeholder={
              isChat && chat.messages.length
                ? t('followUpPlaceholder')
                : placeholder
            }
            aria-label={t('placeholder', { context: productType })}
            maxLength={500}
            disabled={chat.blocked}
            hint={hint}
          />
        </footer>
      )}
    </section>
  );
}
